// src/components/GiustificazioniPresenzePanel.js
// ═══════════════════════════════════════════════════════════════════════════
// GIUSTIFICAZIONI DELLE TIMBRATURE MANCANTI — chi approva i cartellini (ott 2026, W419)
// ═══════════════════════════════════════════════════════════════════════════
// In cima ad Approvazione Cartellini: le giustificazioni da decidere (Accetta / Respingi
// con il motivo) e le ultime decise. Accettare NON corregge gli orari: se servono, si
// correggono nel cartellino (correzione tardiva tracciata). Nessuno decide la propria.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { decidiGiustificazione, eDecisore } from "../utils/giustificazioniPresenze";
import { dataLungaInizio, TESTO_MANCANZA } from "../utils/obbligoPresenze";
import { parseData } from "../utils/periodoLavorativo";

const tempo = (v) => (v?.toMillis ? v.toMillis() : 0);

export default function GiustificazioniPresenzePanel({ orgId, approvatori = [] }) {
  const { currentUser, userProfile, isPlatformOwner } = useAuth();
  const { addToast } = useToast();
  const [abilitazioni, setAbilitazioni] = useState({});
  useEffect(() => {
    if (!orgId) return undefined;
    return onSnapshot(doc(db, "abilitazioniSezioni", orgId), s => setAbilitazioni(s.exists() ? s.data() : {}), () => setAbilitazioni({}));
  }, [orgId]);
  const decide = isPlatformOwner || eDecisore({ ...userProfile, uid: currentUser?.uid }, { abilitazioni, approvatori });

  const [elenco, setElenco] = useState([]);
  useEffect(() => {
    if (!orgId || !decide) { setElenco([]); return undefined; }
    return onSnapshot(query(collection(db, "giustificazioniPresenze"), where("orgId", "==", orgId)),
      s => setElenco(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setElenco([]));
  }, [orgId, decide]);

  const daDecidere = useMemo(() => elenco.filter(g => g.stato === "inviata").sort((a, b) => (a.data || "").localeCompare(b.data || "")), [elenco]);
  const decise = useMemo(() => elenco.filter(g => g.stato !== "inviata").sort((a, b) => tempo(b.decisoIl) - tempo(a.decisoIl)).slice(0, 10), [elenco]);
  const [aperte, setAperte] = useState(false);
  const [respingi, setRespingi] = useState(null);   // id
  const [nota, setNota] = useState("");
  const [lavoro, setLavoro] = useState(null);

  if (!decide || (!daDecidere.length && !decise.length)) return null;
  const decidi = async (g, esito) => {
    setLavoro(g.id);
    try {
      await decidiGiustificazione({ giustificazione: g, esito, nota: esito === "respinta" ? nota : "", decisore: { uid: currentUser.uid, nome: userProfile?.displayName || currentUser?.displayName || "" } });
      addToast(esito === "accettata" ? "Giustificazione accettata ✅" : "Giustificazione respinta", esito === "accettata" ? "success" : "info");
      setRespingi(null); setNota("");
    } catch (e) { addToast(e?.message || "Operazione non riuscita", "error"); }
    setLavoro(null);
  };
  const giorno = (g) => { const d = parseData(g.data); return d ? dataLungaInizio(d) : g.data; };
  const pill = (bg, col) => ({ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: bg, color: col });
  const btn = (bg, col, bordo) => ({ padding: "6px 12px", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", background: bg, color: col, border: bordo || "none" });

  return (
    <section aria-label="Giustificazioni delle timbrature mancanti" style={{ background: "#fff", border: "1px solid #fde68a", borderRadius: 12, padding: "14px 16px", marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <b style={{ fontSize: 14, color: "#92400e", flex: 1 }}>🕐 Giustificazioni delle timbrature mancanti {daDecidere.length ? `(${daDecidere.length} da decidere)` : ""}</b>
        {decise.length > 0 && <button type="button" onClick={() => setAperte(v => !v)} style={btn("#fff", "#6b7280", "1px solid #e5e7eb")}>{aperte ? "Nascondi le ultime decise" : "Ultime decise"}</button>}
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 10px" }}>Accettare non cambia gli orari: se servono, correggili nel cartellino della persona (la correzione resta tracciata).</div>
      <div style={{ display: "grid", gap: 8 }}>
        {daDecidere.map(g => {
          const mia = g.uid === currentUser?.uid;
          return (
            <div key={g.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fffbeb" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <b style={{ fontSize: 13.5, color: "#1e1b4b" }}>{g.nome || "—"}</b>
                <span style={{ fontSize: 13, color: "#374151" }}>{giorno(g)}</span>
                <span style={pill("#fee2e2", "#b91c1c")}>{TESTO_MANCANZA[g.mancanza] || "timbratura mancante"}</span>
              </div>
              <div style={{ fontSize: 13, color: "#374151", marginTop: 6 }}>«{g.motivo}»</div>
              {mia ? <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>È una tua giustificazione: la decide un'altra persona.</div>
                : respingi === g.id ? (
                  <div style={{ marginTop: 8 }}>
                    <label htmlFor={`resp-${g.id}`} style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>Perché la respingi? (la persona lo legge)</label>
                    <input id={`resp-${g.id}`} value={nota} onChange={e => setNota(e.target.value)} maxLength={500}
                      placeholder="Es. quel giorno non risulti nel giornale di cantiere: inserisci gli orari"
                      style={{ width: "100%", boxSizing: "border-box", marginTop: 4, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 13, fontFamily: "inherit" }} />
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <button type="button" disabled={lavoro === g.id || nota.trim().length < 3} onClick={() => decidi(g, "respinta")}
                        style={{ ...btn("#dc2626", "#fff"), opacity: lavoro === g.id || nota.trim().length < 3 ? .5 : 1 }}>Respingi</button>
                      <button type="button" onClick={() => { setRespingi(null); setNota(""); }} style={btn("#fff", "#374151", "1px solid #d1d5db")}>Annulla</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button type="button" disabled={lavoro === g.id} onClick={() => decidi(g, "accettata")} style={btn("#059669", "#fff")}>Accetta</button>
                    <button type="button" onClick={() => { setRespingi(g.id); setNota(""); }} style={btn("#fff", "#dc2626", "1px solid #fca5a5")}>Respingi…</button>
                  </div>
                )}
            </div>
          );
        })}
        {!daDecidere.length && <div style={{ fontSize: 12.5, color: "#6b7280" }}>Nessuna giustificazione da decidere.</div>}
        {aperte && decise.map(g => (
          <div key={g.id} style={{ border: "1px solid #f3f4f6", borderRadius: 10, padding: "8px 12px", fontSize: 12.5, color: "#374151" }}>
            <b>{g.nome || "—"}</b> · <span>{giorno(g)}</span> · «{g.motivo}» ·{" "}
            <span style={g.stato === "accettata" ? pill("#ecfdf5", "#047857") : pill("#fee2e2", "#b91c1c")}>{g.stato}</span>
            {g.decisoDaNome ? <span style={{ color: "#9ca3af" }}> da {g.decisoDaNome}</span> : null}
            {g.notaDecisione ? <span style={{ color: "#6b7280" }}> — «{g.notaDecisione}»</span> : null}
          </div>
        ))}
      </div>
    </section>
  );
}
